/**
 * Shared icon components for the landing app.
 *
 * Two coexisting styles, both hand-rolled (no lucide-react dep):
 *
 * 1. **Filled Heroicons** – 20x20 viewBox, `fill="currentColor"`. Default
 *    library style for compact UI icons (Check, X, Search, Star, LinkedIn).
 * 2. **Stroked Lucide-parity** – 24x24 viewBox, `fill="none"` +
 *    `stroke="currentColor"` + `strokeWidth`. Used for the larger hero
 *    affordances and dashboard-parity icons.
 *
 * Every icon accepts an optional `className` so consumers can override the
 * default size or recolor via Tailwind classes. Icons that have multiple
 * visual variants (e.g., `ArrowRightIcon`, `CheckCircleIcon`) expose a
 * `variant` prop instead of forking into separate exports.
 *
 * Dispatcher icons (`AnalysisIcon`, `StatusIcon`, etc.) take a discriminator
 * prop and return different SVGs per variant. They live in this directory so
 * the `function *Icon` regression guard can enforce a single canonical
 * location for everything that ends in `Icon`.
 *
 * @example
 * ```tsx
 * import { CheckIcon, MousePointerClickIcon } from '@/components/icons';
 *
 * <CheckIcon className="h-5 w-5 text-green-500" />
 * <MousePointerClickIcon className="h-5 w-5 text-slate-700" />
 * ```
 *
 * @module
 */

export { AlertCircleIcon } from './AlertCircleIcon';
export { AlertIcon } from './AlertIcon';
export { AnalysisIcon } from './AnalysisIcon';
export { ArrowDownIcon } from './ArrowDownIcon';
export { ArrowIcon } from './ArrowIcon';
export { ArrowLeftIcon } from './ArrowLeftIcon';
export { ArrowOutIcon } from './ArrowOutIcon';
export { ArrowRightIcon } from './ArrowRightIcon';
export { BookOpenIcon } from './BookOpenIcon';
export { CalendarIcon } from './CalendarIcon';
export { ChatGPTGlyph } from './ChatGPTGlyph';
export { CheckIcon } from './CheckIcon';
export { CheckCircleIcon } from './CheckCircleIcon';
export { ClipboardCheckIcon } from './ClipboardCheckIcon';
export { CloudSlashIcon } from './CloudSlashIcon';
export { DownloadIcon } from './DownloadIcon';
export { ChevronDownIcon } from './ChevronDownIcon';
export { ChevronDownSmallIcon } from './ChevronDownSmallIcon';
export { ChevronIcon } from './ChevronIcon';
export { ClockIcon } from './ClockIcon';
export { ExternalLinkIcon } from './ExternalLinkIcon';
export { GoogleGlyph } from './GoogleGlyph';
export { LightbulbIcon } from './LightbulbIcon';
export { LinkedInIcon } from './LinkedInIcon';
export { LoadingSpinner } from './LoadingSpinner';
export { LockIcon } from './LockIcon';
export { MailIcon } from './MailIcon';
export { MessageIcon } from './MessageIcon';
export { MousePointerClickIcon } from './MousePointerClickIcon';
export { PillArrowIcon } from './PillArrowIcon';
export { PlusIcon } from './PlusIcon';
export { RefreshIcon } from './RefreshIcon';
export { SearchIcon } from './SearchIcon';
export { SparklesIcon } from './SparklesIcon';
export { StarIcon } from './StarIcon';
export { TrendingDownIcon } from './TrendingDownIcon';
export { UserIcon } from './UserIcon';
export { WarningIcon } from './WarningIcon';
export { XCircleIcon } from './XCircleIcon';
export { XIcon } from './XIcon';
